import type { CarrierEnabledScope } from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Site-query constraint derived from a carrier's enabled scope for one space.
 *
 * - `none`      → the carrier has no enabled scope in this space; no sites apply.
 * - `allExcept` → the carrier is enabled on all sites of the space; every site is
 *                 available except the explicitly disabled ones (`excludeIds`).
 * - `only`      → the carrier uses an allowlist; only the enabled sites (`ids`) apply.
 */
export type CarrierSiteFilter =
  { mode: 'none' } | { excludeIds: string[]; mode: 'allExcept' } | { ids: string[]; mode: 'only' };

// ─── Helper ─────────────────────────────────────────────────────────────────────

/**
 * Derives which sites are enabled for a carrier within a given space, from the
 * carrier's `enabledScopes`. A site counts as enabled when it is not explicitly
 * disabled in the space scope.
 *
 * Shared by the carrier "enabled sites" page and the driver scope selector so both
 * apply the same enabled/disabled rule.
 */
export function getCarrierSiteFilterForSpace(enabledScopes: CarrierEnabledScope[], spaceId: string): CarrierSiteFilter {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope) {
    return { mode: 'none' };
  }

  const siteSpecifics = scope.siteSpecifics ?? [];

  if (scope.enabledOnAllSites) {
    return { excludeIds: siteSpecifics.filter((ss) => ss.disabled).map((ss) => ss.siteId), mode: 'allExcept' };
  }

  return { ids: siteSpecifics.filter((ss) => !ss.disabled).map((ss) => ss.siteId), mode: 'only' };
}
